import { useEffect, useRef, useState } from 'react'
import Router from 'next/router'
import { ChatLoadingScreen } from '@educator/features/ExecutiveChat/components/ChatLoadingScreen'

// Topic/module chat routes, e.g.
//   /learning/subjects/<subject>/modules/<module>[/topics/<topic>]
// `next/link` blocks the page swap on the destination's getServerSideProps, so
// without feedback a topic click looks dead for the whole server round-trip.
// We cover that window with the chat shell so the chat appears to open
// immediately (JAR-2358 follow-up: perceived loading). The overlay fires when
// navigating into ANY lesson — including lesson → lesson jumps while already in
// the chat — so every session open shows the same subject loading screen.
const CHAT_ROUTE_RE = /\/learning\/subjects\/([^/]+)\/modules\//

// Pull the subject slug out of a chat route so the loading screen can play that
// subject's square animation. Returns undefined for non-chat URLs — which also
// serves as the "is this a lesson route?" test — in which case the loader shows
// the static brand mark.
export const getChatSubjectSlug = (url: string): string | undefined =>
  CHAT_ROUTE_RE.exec(url)?.[1]

// The Up Next "Continue" card runs its own FLIP expand animation into the next
// lesson (see NextTopicCard). That animation owns the visual hand-off, so the
// overlay must not cover it. NextTopicCard calls suppressNextChatOverlay() right
// before it pushes; the overlay consumes the flag on the next routeChangeStart
// and skips that one navigation. Every other lesson open still shows the loader.
let suppressNextNavigation = false
let suppressTimer: ReturnType<typeof setTimeout> | undefined
export const suppressNextChatOverlay = () => {
  suppressNextNavigation = true
  if (suppressTimer) clearTimeout(suppressTimer)
  // Safety: never let a stale suppression outlive the navigation it was meant
  // for (e.g. the push was cancelled) and swallow a later, unrelated lesson open.
  suppressTimer = setTimeout(() => {
    suppressNextNavigation = false
  }, 2000)
}

// Minimum time the loader stays up once shown. A fast server round-trip would
// otherwise flash the overlay for a few frames — long enough to be a jarring
// blink, too short to read as intentional. Holding it briefly makes the
// hand-off feel deliberate and gives the subject animation a beat to register.
const MIN_VISIBLE_MS = 1500

// Safety valve: the overlay is a full-viewport layer that swallows all input
// while shown. It normally hides on routeChangeComplete/Error, but if a
// navigation never settles (e.g. server props hang with no response, so neither
// event ever fires) the overlay would lock the user out until a hard refresh.
// Auto-hide after this long so a stuck navigation degrades to the previous
// screen instead of a dead app.
const MAX_VISIBLE_MS = 15000

/**
 * Renders the chat loading shell while navigating into a lesson — from another
 * screen (e.g. the subject modal) or from another lesson. Every session open
 * shows the subject loading screen, held for a minimum time so a fast
 * navigation doesn't flash.
 */
export const ChatTransitionOverlay = () => {
  const [visible, setVisible] = useState(false)
  const [subjectSlug, setSubjectSlug] = useState<string | undefined>(undefined)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )
  const shownAtRef = useRef(0)

  useEffect(() => {
    const clearTimer = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = undefined
      }
    }
    const hide = () => {
      clearTimer()
      setVisible(false)
    }
    // Hide once the navigation settles, but never before MIN_VISIBLE_MS has
    // elapsed since the loader appeared — otherwise a fast route change blinks
    // the overlay for a few frames.
    const requestHide = () => {
      clearTimer()
      const remaining = MIN_VISIBLE_MS - (Date.now() - shownAtRef.current)
      if (remaining <= 0) {
        setVisible(false)
        return
      }
      timeoutRef.current = setTimeout(hide, remaining)
    }
    const handleStart = (url: string) => {
      // Consume the suppression flag on the very next navigation, whatever it
      // is, so it can never leak into an unrelated later one.
      const suppressed = suppressNextNavigation
      suppressNextNavigation = false
      if (suppressTimer) {
        clearTimeout(suppressTimer)
        suppressTimer = undefined
      }
      const subject = getChatSubjectSlug(url)
      if (subject !== undefined && !suppressed) {
        setSubjectSlug(subject)
        setVisible(true)
        shownAtRef.current = Date.now()
        clearTimer()
        timeoutRef.current = setTimeout(hide, MAX_VISIBLE_MS)
      }
    }

    // Subscribe once, on the router singleton. `useRouter()` hands out a new
    // object on every app render, and Next emits routeChangeComplete before
    // the re-render's effects run: an effect keyed on that object would take
    // the deferred hide (scheduled by the old handler) down with its cleanup
    // and leave the overlay up for good after any arrival under MIN_VISIBLE_MS.
    Router.events.on('routeChangeStart', handleStart)
    Router.events.on('routeChangeComplete', requestHide)
    Router.events.on('routeChangeError', requestHide)

    return () => {
      Router.events.off('routeChangeStart', handleStart)
      Router.events.off('routeChangeComplete', requestHide)
      Router.events.off('routeChangeError', requestHide)
      clearTimer()
    }
  }, [])

  if (!visible) return null

  return (
    <div
      data-testid='chat-transition-overlay'
      className='fixed inset-0 z-[100]'
    >
      <ChatLoadingScreen subjectSlug={subjectSlug} />
    </div>
  )
}
